import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  Typography,
  IconButton,
  CircularProgress,
  Modal,
  MenuItem,
  Select,
} from "@mui/material";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import CloseIcon from "@mui/icons-material/Close";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
} from "recharts";
import { BASE_URL } from "../../../components/BaseUrl";
import { useSelector } from "react-redux";
import { decryptToken } from "../../../Utlis/Encryption";
import CustomButton from "../../../components/CustomButton";
import mapicon from "../../../assets/Dashboard/mapicon.svg";
import empty from "../../../assets/empty.png";
import { useTranslation } from "react-i18next";
const ScheduleCompliance = ({ dateRange }) => {
  const { company, token } = useSelector((state) => state.auth);
  const companyId = company?.id;
  const decryptedToken = token ? decryptToken(token) : null;
const {t} =useTranslation()
  const [workers, setWorkers] = useState([]);
  const [selectedWorker, setSelectedWorker] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [openSummaryModal, setOpenSummaryModal] = useState(false);
//   const [dateRange, setDateRange] = useState({ startDate: "", endDate: "" });
  const [tempFilter, setTempFilter] = useState({
    startDate: "",
    endDate: "",
    worker: "",
  });
  const [isFiltered, setIsFiltered] = useState(false);

  const COLORS = ["#42A5F5", "#66BB6A", "#FFA726", "#EF5350"];

  const getCurrentMonthRange = () => {
    const now = new Date();
    const first = new Date(now.getFullYear(), now.getMonth(), 1);
    const last = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    const fmt = (d) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
        d.getDate()
      ).padStart(2, "0")}`;
    return { startDate: fmt(first), endDate: fmt(last) };
  };

  const formatHours = (value) => {
    const num = parseFloat(value) || 0;
    const sign = num < 0 ? "-" : "";
    const abs = Math.abs(num);
    let hrs = Math.floor(abs);
    let mins = Math.round((abs - hrs) * 60);
    if (mins === 60) {
      hrs += 1;
      mins = 0;
    }
    return mins > 0 ? `${sign}${hrs}h ${mins}m` : `${sign}${hrs}h`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };

  const fetchWorkers = async () => {
    console.log("🔹 Fetching workers for companyId:", companyId);
    try {
      const res = await fetch(
        `${BASE_URL}/public/workers?company_id=${companyId}&no_pagination=true`
      );
      const data = await res.json();
      const list = data.data?.records || [];
      setWorkers(list);
      console.log("✅ Workers fetched:", list.length);
      return list;
    } catch (e) {
      console.error("❌ Error fetching workers:", e);
      return [];
    }
  };
  const fetchReport = async (start, end, workerId) => {
    console.log("🔹 Fetching report with params:", {
      type: "schedule_compliance",
      companyId,
      workerId,
      start,
      end,
    });
    try {
      const res = await fetch(`${BASE_URL}/public/reports/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          type: "schedule_compliance",
          startDate: start,
          endDate: end,
          workerId,
          companyId,
          showChartData: true,
        }),
      });

      const result = await res.json();
      console.log("🔹 Report API response JSON:", result);

      if (!result.error && result.data?.data?.length > 0) {
        console.log("✅ Report data found:", result.data.data[0]);
        setReportData(result.data.data[0]);
      } else {
        console.warn("⚠️ Report API returned no data or error flag true.");
        setReportData({});
      }
    } catch (e) {
      console.error("❌ Error fetching report:", e);
      setReportData({});
    }
  };

  useEffect(() => {
    const init = async () => {
      if (!companyId) return;
      const range = getCurrentMonthRange();
    //   setDateRange(range);
      const list = await fetchWorkers();
      if (list?.length > 0) {
        const first = list[0].id;
        setSelectedWorker(first);
        await fetchReport(range.startDate, range.endDate, first);
      } else {
        setReportData({});
      }
    };
    init();
  }, [companyId]);





const handleApply = () => {
  setOpenModal(false);
  setSelectedWorker(tempFilter.worker);
  setIsFiltered(true);
  fetchReport(dateRange.startDate, dateRange.endDate, tempFilter.worker);
};
const handleClearFilter = () => {
  if (workers.length > 0) {
    setSelectedWorker(workers[0]?.id);
    setIsFiltered(false);
    fetchReport(dateRange.startDate, dateRange.endDate, workers[0]?.id);
  }
};

  if (!reportData) {
    console.log("⏳ Waiting for reportData to load...");
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="70vh">
        <CircularProgress />
      </Box>
    );
  }

  const attendanceData = [
    {
       name: t("attendanceSummary"),
      "Total Days": parseInt(reportData.total_scheduled_days),
      "Days Attended": parseInt(reportData.days_attended),
      "Days Absent": parseInt(reportData.days_absent),
    },
  ];

  // Actual + |Variance| = Scheduled, so only these two are plotted as slices
  // (including Scheduled Hours as a third slice would double-count the total
  // and feed a negative value into the pie, breaking the percentages/colors).
const compliancePie = [
  { name: t("actualHours"), value: parseFloat(reportData.total_actual_hours) || 0 },
  { name: t("hoursVariance"), value: Math.abs(parseFloat(reportData.total_hours_variance) || 0) },
];

  // Near-horizontal slices (left OR right) get pushed toward the top instead
  // of extending sideways, so the label text stays inside the card. Bent
  // labels are stacked by slice index so two near-opposite slices (e.g. a
  // thin sliver vs. the rest of the ring) don't land on the same row.
  const renderPieLabel = ({ cx, cy, midAngle, outerRadius, value, name, fill, index }) => {
    const RADIAN = Math.PI / 180;
    const cos = Math.cos(-midAngle * RADIAN);
    const sin = Math.sin(-midAngle * RADIAN);
    const arcX = cx + outerRadius * cos;
    const arcY = cy + outerRadius * sin;

    let bendX, bendY, labelX, labelY, anchor;
    if (Math.abs(cos) > 0.6) {
      bendX = cx + (outerRadius + 10) * cos * 0.4;
      bendY = cy - outerRadius - 10 - index * 18;
      labelX = bendX;
      labelY = bendY - 8;
      anchor = "middle";
    } else {
      const radius = outerRadius + 20;
      bendX = cx + radius * cos;
      bendY = cy + radius * sin;
      labelX = bendX + (cos >= 0 ? 6 : -6);
      labelY = bendY;
      anchor = cos >= 0 ? "start" : "end";
    }

    return (
      <g>
        <path d={`M${arcX},${arcY}L${bendX},${bendY}`} stroke={fill} fill="none" />
        <text x={labelX} y={labelY} textAnchor={anchor} dominantBaseline="central" fontSize={12} fill={fill}>
          {`${name}: ${formatHours(value)}`}
        </text>
      </g>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: "18px",
              color: "#003149",
              fontFamily: "Poppins, sans-serif",
            }}
          >
           {t("reportsScreen.workerScheduleCompliance")}
          </Typography>
     
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box
            sx={{
              border: "1px solid #C4C4C4",
              borderRadius: 1,
              px: 2,
              py: 0.8,
              bgcolor: "#F9FAFB",
              color: "#003149",
              fontSize: 14,
              minWidth: 260,
              textAlign: "center",
            }}
          >
            {workers.length > 0 &&
              `${workers.find((w) => w.id === selectedWorker)?.first_name || ""} ${
                workers.find((w) => w.id === selectedWorker)?.last_name || ""
              } (${workers.find((w) => w.id === selectedWorker)?.email || ""}) | ${
                dateRange.startDate
                  ? `${formatDate(dateRange.startDate)} – ${formatDate(dateRange.endDate)}`
                  : ""
              }`}
          </Box>

          <Box
            sx={{
              position: "relative",
              width: 36,
              height: 36,
              borderRadius: 1,
              border: "1px solid #C4C4C4",
              bgcolor: isFiltered ? "#1976d2" : "transparent",
              color: isFiltered ? "#fff" : "#003149",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <IconButton
              onClick={() => {
                setTempFilter({
                  startDate: dateRange.startDate,
                  endDate: dateRange.endDate,
                  worker: selectedWorker,
                });
                setOpenModal(true);
              }}
              sx={{
                width: "100%",
                height: "100%",
                color: "inherit",
                "&:hover": {
                  backgroundColor: isFiltered ? "#1565c0" : "#1976d220",
                },
              }}
            >
              <FilterAltIcon />
            </IconButton>
            {isFiltered && (
              <IconButton
                size="small"
                onClick={handleClearFilter}
                sx={{
                  position: "absolute",
                  top: "-5px",
                  right: "-5px",
                  backgroundColor: "#fff",
                  border: "1px solid #C4C4C4",
                  width: 18,
                  height: 18,
                }}
              >
                <CloseIcon sx={{ fontSize: 13, color: "#333" }} />
              </IconButton>
            )}
          </Box>
        </Box>
      </Box>

{/* Charts */}
<Box
  sx={{
    display: "flex",
    flexWrap: "wrap",
    gap: 3,
    mt: 2,
  }}
>
  {/* Attendance Chart */}
  <Card
    sx={{
      flex: "1 1 48%",
      p: 2,
      border: "2px solid #091E4224",
      borderRadius: 2,
      boxShadow: "none",
    }}
  >
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        mb: 1,
      }}
    >
      <Typography
        variant="h6"
        sx={{
          fontSize: "15px",
          color: "#44546F",
          fontWeight: 500,
          fontFamily: "Poppins, sans-serif",
        }}
      >
      {t("reportsScreen.employee_schedule_compliance_report")}
      </Typography>
      <IconButton onClick={() => setOpenSummaryModal(true)}>
        <img src={mapicon} alt="map" style={{ width: 33, height: 33 }} />
      </IconButton>
    </Box>

    {parseFloat(reportData.total_scheduled_days) > 0 ||
    parseFloat(reportData.days_attended) > 0 ||
    parseFloat(reportData.days_absent) > 0 ? (
      <ResponsiveContainer width="100%" height={320}>
        <BarChart data={attendanceData} barCategoryGap="35%" barGap={10}>
          <XAxis dataKey="name" />
          <YAxis />
          <Tooltip />
          <Legend />
          <Bar dataKey={t("reportsScreen.totalDays")} fill="#42A5F5" barSize={30} />
          <Bar dataKey={t("reportsScreen.daysAttended")} fill="#66BB6A" barSize={30} />
          <Bar dataKey={t("reportsScreen.daysAbsent")} fill="#EF5350" barSize={30} />
        </BarChart>
      </ResponsiveContainer>
    ) : (
      <Box
        sx={{
          height: 320,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <img src={empty} alt="No data" style={{ width: 180, opacity: 0.7 }} />
      </Box>
    )}
  </Card>

  {/* Compliance Chart */}
  <Card
    sx={{
      flex: "1 1 48%",
      p: 2,
      border: "2px solid #091E4224",
      borderRadius: 2,
      boxShadow: "none",
    }}
  >
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        mb: 1,
      }}
    >
      <Typography
        variant="h6"
        sx={{
          fontSize: "15px",
          color: "#44546F",
          fontWeight: 500,
          fontFamily: "Poppins, sans-serif",
        }}
      >
      {t("reportsScreen.hourly_compliance_worker_log")}
      </Typography>
      <IconButton onClick={() => setOpenSummaryModal(true)}>
        <img src={mapicon} alt="map" style={{ width: 33, height: 33 }} />
      </IconButton>
    </Box>

    {parseFloat(reportData.total_scheduled_hours) > 0 ||
    parseFloat(reportData.total_actual_hours) > 0 ? (
      <ResponsiveContainer width="100%" height={320}>
        <PieChart margin={{ top: 30, right: 20, bottom: 20, left: 20 }}>
          <Pie
            data={compliancePie}
            cx="50%"
            cy="50%"
            innerRadius={75}
            outerRadius={110}
            paddingAngle={4}
            dataKey="value"
            labelLine={false}
            label={renderPieLabel}
          >
            {compliancePie.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={COLORS[index % COLORS.length]}
                stroke="#fff"
                strokeWidth={2}
              />
            ))}
          </Pie>
          <Tooltip formatter={(value, name) => [formatHours(value), name]} />
          <Legend
            verticalAlign="bottom"
            align="center"
            iconType="circle"
            formatter={(value, entry) => `${value}: ${formatHours(entry.payload.value)}`}
          />
        </PieChart>
      </ResponsiveContainer>
    ) : (
      <Box
        sx={{
          height: 320,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <img src={empty} alt="No data" style={{ width: 180, opacity: 0.7 }} />
      </Box>
    )}
  </Card>
</Box>



{/* Summary Modal */}
<Modal open={openSummaryModal} onClose={() => setOpenSummaryModal(false)}>
  <Box
    sx={{
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      bgcolor: "white",
      boxShadow: 24,
      p: 3,
      borderRadius: 3,
      width: 500,
      maxHeight: "80vh",
      overflowY: "auto",
      scrollbarWidth: "none",
      msOverflowStyle: "none",
      "&::-webkit-scrollbar": { display: "none" },
    }}
  >
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        mb: 2,
      }}
    >
      <Typography
        variant="h6"
        sx={{
          fontWeight: 600,
          color: "#003149",
          fontFamily: "Poppins, sans-serif",
        }}
      >
{t("reportsScreen.summaryData")}
      </Typography>
      <IconButton onClick={() => setOpenSummaryModal(false)}>
        <CloseIcon />
      </IconButton>
    </Box>

    {reportData ? (
      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          fontFamily: "Poppins, sans-serif",
          fontSize: "14px",
        }}
      >
        <thead>
          <tr style={{ background: "#f5f5f5", textAlign: "left" }}>
            <th style={{ padding: "10px" }}>{t("reportsScreen.metric")}</th>
            <th style={{ padding: "10px", textAlign: "right" }}>{t("reportsScreen.value")}</th>
          </tr>
        </thead>
        <tbody>
          {Object.entries(reportData).map(([key, value]) => (
            <tr key={key}>
              <td
                style={{
                  padding: "8px 10px",
                  borderBottom: "1px solid #eee",
                  textTransform: "capitalize",
                }}
              >
                {key.replace(/_/g, " ")}
              </td>
              <td
                style={{
                  padding: "8px 10px",
                  borderBottom: "1px solid #eee",
                  textAlign: "right",
                }}
              >
                {/hours/i.test(key) ? formatHours(value) : value}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    ) : (
      <Typography>{t("reportsScreen.noData")}</Typography>
    )}
  </Box>
</Modal>


      {/* Filter Modal */}
      <Modal open={openModal} onClose={() => setOpenModal(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "white",
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
            width: 550,
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: "#003149" }}>
    {t("reportsScreen.filterByDateWorker")}
            </Typography>
            <IconButton onClick={() => setOpenModal(false)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* <label className="field_label">Start Date</label>
          <input
            type="date"
            value={tempFilter.startDate}
            onChange={(e) => setTempFilter((p) => ({ ...p, startDate: e.target.value }))}
            className="project_input"
            style={{ width: "100%", height: "42px", marginTop: "6px" }}
          />

          <label className="field_label" style={{ marginTop: "16px" }}>
            End Date
          </label>
          <input
            type="date"
            value={tempFilter.endDate}
            onChange={(e) => setTempFilter((p) => ({ ...p, endDate: e.target.value }))}
            className="project_input"
            style={{ width: "100%", height: "42px", marginTop: "6px" }}
          /> */}

          <label className="field_label" style={{ marginTop: "16px" }}>
            {t("reportsScreen.worker")}   </label>
          <Select
            value={tempFilter.worker}
            onChange={(e) => setTempFilter((p) => ({ ...p, worker: e.target.value }))}
            fullWidth
            sx={{
              mt: 1,
              height: "42px",
              fontSize: "15px",
              fontFamily: "Poppins, sans-serif",
            }}
          >
            {workers.map((w) => (
              <MenuItem
                key={w.id}
                value={w.id}
                sx={{ fontSize: "13px", fontFamily: "Poppins, sans-serif" }}
              >
                {`${w.first_name || ""} ${w.last_name || ""} (${w.email || "no email"})`}
              </MenuItem>
            ))}
          </Select>

          <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
            <CustomButton label={t("reportsScreen.apply")} size="medium" onClick={handleApply} />
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default ScheduleCompliance;
